import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Timer } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Switch } from '../ui/switch'
import { useToast } from '../ui/use-toast'
import { useTimerNotificationsEnabled } from '../../hooks/useTimerNotificationsEnabled'

type Permission = 'default' | 'granted' | 'denied' | 'unsupported'

function readPermission(): Permission {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') return 'unsupported'
  return Notification.permission as Permission
}

export function TimerNotificationsCard() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [enabled, setEnabled] = useTimerNotificationsEnabled()
  const [permission, setPermission] = useState<Permission>(() => readPermission())

  useEffect(() => {
    // Permission can change in another tab / via OS settings; re-check on focus.
    function refresh() {
      setPermission(readPermission())
    }
    window.addEventListener('focus', refresh)
    return () => window.removeEventListener('focus', refresh)
  }, [])

  const supported = permission !== 'unsupported'
  const denied = permission === 'denied'

  async function handleToggle(value: boolean) {
    if (!supported) return
    if (value && permission === 'default') {
      const result = await Notification.requestPermission()
      setPermission(result as Permission)
      if (result !== 'granted') {
        toast({
          title: t('notificationSettings.timer.permissionDenied'),
          variant: 'destructive',
        })
        return
      }
    }
    if (value && permission === 'denied') {
      toast({
        title: t('notificationSettings.timer.permissionDenied'),
        variant: 'destructive',
      })
      return
    }
    setEnabled(value)
    toast({ title: t('notificationSettings.saved') })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Timer className="h-5 w-5" />
          {t('notificationSettings.timer.title', 'Workout timer notifications')}
        </CardTitle>
        <CardDescription>
          {t(
            'notificationSettings.timer.description',
            'Get a vibration + system notification 15 seconds before each phase change and at the transition itself when the app is in the background. Off by default.',
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-4">
          <span className="font-medium">
            {t('notificationSettings.timer.toggleLabel', 'Background timer notifications')}
          </span>
          <Switch
            checked={enabled && permission === 'granted'}
            onCheckedChange={handleToggle}
            disabled={!supported || denied}
            aria-label={t('notificationSettings.timer.toggleLabel', 'Background timer notifications') as string}
          />
        </div>
        {!supported && (
          <p className="text-sm text-muted-foreground mt-3">
            {t('notificationSettings.timer.unsupported', 'This browser does not support background notifications.')}
          </p>
        )}
        {denied && (
          <p className="text-sm text-destructive mt-3">
            {t(
              'notificationSettings.timer.permissionDeniedHint',
              'Notifications are blocked at the browser level. Enable them in your browser settings to use this feature.',
            )}
          </p>
        )}
      </CardContent>
    </Card>
  )
}
